// Phase-1 Reports page — unified report runner. Hitting "Run Report" no longer
// renders the grid inline; instead it opens a NEW workspace tab (via useFormTab)
// containing the result grid + an Export-to-Excel button. The config tab stays
// open so users can tweak filters and run additional reports without losing
// previous results.

import { useEffect, useMemo, useState } from "react";
import { api, downloadReportAsync } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Download, Play, Filter as FilterIcon, FileBarChart2, Layers, RefreshCw, Search, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useFormTab } from "@/hooks/useFormTab";
import { SearchableSelect } from "@/components/SearchableSelect";

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

// ─────────────────────────────────────────────────────────────────────────
// Preset date-range helpers — Indian Financial Year (Apr 1 → Mar 31).
// Every helper returns { from_date, to_date } as ISO YYYY-MM-DD strings.
// ─────────────────────────────────────────────────────────────────────────
const _iso = (d) => d.toISOString().slice(0, 10);
const _fyStartYear = (d) => (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1);
const _fy = (startYear) => ({
  from_date: `${startYear}-04-01`,
  to_date:   `${startYear + 1}-03-31`,
  label:     `FY ${String(startYear).slice(-2)}-${String(startYear + 1).slice(-2)}`,
});
const DATE_PRESETS = [
  { id: "current_month", label: "Current Month", get: () => {
      const d = new Date();
      return { from_date: _iso(new Date(d.getFullYear(), d.getMonth(), 1)),
               to_date:   _iso(d) };
  }},
  { id: "last_month", label: "Last Month", get: () => {
      const d = new Date();
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last  = new Date(d.getFullYear(), d.getMonth(), 0);
      return { from_date: _iso(first), to_date: _iso(last) };
  }},
  { id: "current_quarter", label: "Current Quarter", get: () => {
      const d = new Date();
      const q = Math.floor(d.getMonth() / 3);
      return { from_date: _iso(new Date(d.getFullYear(), q * 3, 1)),
               to_date:   _iso(d) };
  }},
  { id: "current_fy", label: "Current FY", get: () => {
      const d = new Date();
      return _fy(_fyStartYear(d));
  }},
  { id: "previous_fy", label: "Previous FY", get: () => {
      const d = new Date();
      return _fy(_fyStartYear(d) - 1);
  }},
  { id: "last_7_days", label: "Last 7 Days", get: () => {
      const to = new Date();
      const from = new Date(); from.setDate(from.getDate() - 6);
      return { from_date: _iso(from), to_date: _iso(to) };
  }},
  { id: "last_30_days", label: "Last 30 Days", get: () => {
      const to = new Date();
      const from = new Date(); from.setDate(from.getDate() - 29);
      return { from_date: _iso(from), to_date: _iso(to) };
  }},
  { id: "ytd", label: "Year to Date", get: () => {
      const d = new Date();
      return { from_date: `${d.getFullYear()}-01-01`, to_date: _iso(d) };
  }},
];

// Module identifiers used by approval_requests — kept in sync with backend
// _MODULE_LABEL map in reports_v2.py. Powers the Audit Report module dropdown.
const AUDIT_MODULES = [
  { id: "sales_invoice",        label: "Sales Invoice" },
  { id: "purchase_invoice",     label: "Purchase Invoice" },
  { id: "estimate",             label: "Estimate" },
  { id: "purchase_order",       label: "Purchase Order" },
  { id: "expense_voucher",      label: "Expense Voucher" },
  { id: "bill_payment_voucher", label: "Bill Payment Voucher" },
  { id: "sales_credit_note",    label: "Sales Credit Note" },
  { id: "sales_debit_note",     label: "Sales Debit Note" },
  { id: "vendor_advance",       label: "Vendor Advance" },
  { id: "work_allotment",       label: "Work Allotment" },
  { id: "jms_entry",            label: "Job Sheet (JMS)" },
  { id: "contractor_bill",      label: "Contractor Bill" },
  { id: "salary_payment",       label: "Salary Payment" },
  { id: "store_rent_payment",   label: "Store Rent Payment" },
  { id: "general_payment",      label: "General Payment" },
  { id: "stock_transfer",       label: "Stock Transfer" },
  { id: "asset_movement",       label: "Asset Movement" },
  { id: "return_voucher",       label: "Return Voucher" },
  { id: "issue_voucher",        label: "Issue Voucher" },
];

export default function ReportsV2() {
  const [catalog, setCatalog] = useState([]);
  const [reportId, setReportId] = useState("");
  const [maxRows, setMaxRows] = useState(3000);
  // Sprint 4.4 — Reports that support PDF export (server-driven list).
  const [pdfCapable, setPdfCapable] = useState([]);

  const [filters, setFilters] = useState({
    from_date: monthStartISO(),
    to_date: todayISO(),
    branch_id: "",
    project_id: "",
    client_id: "",
    vendor_id: "",
    status: "",
    module: "",
    // Sprint 4.4 — Warehouse Reports
    warehouse_id: "",
    item_id: "",
    movement_type: "",
    category: "",
    // 2026-Feb — Line-level free-text filters on the new
    // Salary / Store-Rent / General-Payment registers.
    month: "",
    rent_period: "",
  });

  const [lookup, setLookup] = useState({
    branches: [], projects: [], clients: [], vendors: [],
    warehouses: [], items: [],
  });
  const [running, setRunning] = useState(false);
  // Preset date-range chip currently applied ("" = Custom / user-picked).
  const [datePreset, setDatePreset] = useState("current_month");

  const applyDatePreset = (id) => {
    const preset = DATE_PRESETS.find(p => p.id === id);
    if (!preset) return;
    const range = preset.get();
    setFilters(f => ({ ...f, from_date: range.from_date, to_date: range.to_date }));
    setDatePreset(id);
  };
  // Sprint 4.11.19 — Search box that filters the report picker by name/module/id
  // so admins can find any of the 50+ reports without scrolling.
  const [reportSearch, setReportSearch] = useState("");
  const openFormTab = useFormTab();

  useEffect(() => {
    api.get("/reports/list").then(({ data }) => {
      setCatalog(data.reports || []); setMaxRows(data.max_rows || 3000);
      setPdfCapable(data.pdf_capable || []);
      if (!reportId && data.reports?.length) setReportId(data.reports[0].id);
    }).catch(() => toast.error("Could not load reports catalog"));
    // Lookups for filter dropdowns
    Promise.all([
      api.get("/branches"),
      api.get("/projects-picker"),
      api.get("/clients/picker"),
      api.get("/vendors/picker"),
      api.get("/warehouses/picker").catch(() => ({ data: [] })),
      api.get("/items").catch(() => ({ data: [] })),
    ]).then(([b, p, c, v, w, it]) => setLookup({
      branches: b.data || [], projects: p.data || [], clients: c.data || [], vendors: v.data || [],
      warehouses: Array.isArray(w.data) ? w.data : (w.data?.items || []),
      items: Array.isArray(it.data) ? it.data : (it.data?.items || []),
    })).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const meta = useMemo(() => catalog.find(r => r.id === reportId), [catalog, reportId]);
  const needs = (k) => (meta?.needs || []).includes(k);

  function set(k, v) { setFilters(f => ({ ...f, [k]: v })); }

  async function runReport() {
    if (!reportId) { toast.error("Pick a report"); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/reports/run", { report_id: reportId, ...filters });
      if ((data.rows?.length || 0) >= maxRows) {
        toast.warning(`Showing first ${maxRows} rows. Narrow the filters to see more.`);
      }
      // Snapshot the filters used for this run so the result tab's Export button
      // re-exports exactly what's on screen, regardless of further config changes.
      const runFilters = { ...filters };
      const runReportId = reportId;
      const runLabel = `Report · ${data.name || meta?.name || runReportId}`;
      const runPdfCapable = pdfCapable.includes(runReportId);
      openFormTab({
        // Same path per reportId — re-running the same report refreshes the same
        // tab (avoids stacking duplicate tabs). To compare runs, the user can
        // duplicate-export and pick a different report.
        path: `/reports/result/${runReportId}`,
        label: runLabel,
        icon: FileBarChart2,
        parent: "/reports",
        render: ({ close }) => (
          <ReportResultView
            reportId={runReportId}
            filters={runFilters}
            data={data}
            pdfCapable={runPdfCapable}
            onClose={close}
          />
        ),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run report");
    } finally {
      setRunning(false);
    }
  }

  const groupedCatalog = useMemo(() => {
    const q = (reportSearch || "").trim().toLowerCase();
    const list = q
      ? catalog.filter(r => {
          const hay = `${r.name || ""} ${r.module || ""} ${r.id || ""}`.toLowerCase();
          return hay.includes(q);
        })
      : catalog;
    const out = {};
    for (const r of list) { (out[r.module] ||= []).push(r); }
    return out;
  }, [catalog, reportSearch]);

  const totalFiltered = useMemo(
    () => Object.values(groupedCatalog).reduce((n, l) => n + l.length, 0),
    [groupedCatalog],
  );

  return (
    <div data-testid="reports-v2-page">
      <Toaster />
      <PageHeader
        eyebrow="Reports & Registers"
        title="Reports"
        subtitle={`Configure a report and click Run — the result opens in a new tab with an Excel export button (cap ${maxRows} rows).`}
      />

      <AsyncExportStrip filters={filters} />

      {/* Sprint 4.11.20 — Reports page restructured. Filters + Run sit on
          TOP; the report catalog is rendered HORIZONTALLY below grouped
          by module so the operator can see every report in 2-3 row of
          chip-style cards instead of a 280px vertical sidebar.
          Sprint 4.11.21 — Filters + Run Report block is sticky to the
          tab-strip top (38px) so the operator can scroll through the long
          catalog below without losing access to date/branch/run controls. */}
      <div>
        {/* Filters + Run Report */}
        <div className="bg-white border border-slate-200 px-4 py-3 mb-3 sticky top-[38px] z-20 shadow-sm"
             data-testid="rpt-filters-block">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FilterIcon size={14} className="text-slate-500" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Filters</span>
              {meta && (
                <span className="text-[11px] text-slate-700 ml-3">
                  <span className="text-slate-400">Selected: </span>
                  <span className="font-semibold">{meta.name}</span>
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
            <div className="col-span-2 md:col-span-4 lg:col-span-6 flex items-center flex-wrap gap-1.5 mb-1"
                 data-testid="rpt-date-presets">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mr-1">Quick Range</span>
              {DATE_PRESETS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`rpt-preset-${p.id}`}
                  onClick={() => applyDatePreset(p.id)}
                  className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border transition-colors ${
                    datePreset === p.id
                      ? "bg-[#F97316] border-[#F97316] text-white"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"
                  }`}>
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                data-testid="rpt-preset-custom"
                onClick={() => setDatePreset("")}
                className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border transition-colors ${
                  datePreset === ""
                    ? "bg-slate-700 border-slate-700 text-white"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"
                }`}>
                Custom
              </button>
            </div>
            <Field label="From Date">
              <input type="date" value={filters.from_date}
                onChange={e => { set("from_date", e.target.value); setDatePreset(""); }}
                data-testid="rpt-from-date" className={inp} />
            </Field>
            <Field label="To Date">
              <input type="date" value={filters.to_date}
                onChange={e => { set("to_date", e.target.value); setDatePreset(""); }}
                data-testid="rpt-to-date" className={inp} />
            </Field>
            <Field label="Branch">
              <select value={filters.branch_id} onChange={e => set("branch_id", e.target.value)} className={inp}>
                <option value="">All</option>
                {lookup.branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select>
            </Field>
            <Field label="Project">
              <SearchableSelect
                value={filters.project_id}
                onChange={(id) => set("project_id", id)}
                items={lookup.projects}
                getLabel={(p) => `${p.project_code} · ${p.project_name}`}
                getSearchText={(p) => `${p.project_code || ""} ${p.project_name || ""}`}
                placeholder={needs("project_id") ? "— Pick a project —" : "All"}
                searchPlaceholder="Search project code / name…"
                testid="rpt-project"
              />
            </Field>
            {(needs("client_id") || meta?.module === "Sales" || meta?.module === "Ledger") && (
              <Field label="Client">
                <SearchableSelect
                  value={filters.client_id}
                  onChange={(id) => set("client_id", id)}
                  items={lookup.clients}
                  getLabel={(c) => c.name || ""}
                  placeholder={needs("client_id") ? "— Pick a client —" : "All"}
                  searchPlaceholder="Search client…"
                  testid="rpt-client"
                />
              </Field>
            )}
            {(needs("vendor_id") || meta?.module === "Ledger" || meta?.module === "Purchase" || meta?.module === "Payments") && (
              <Field label="Vendor">
                <SearchableSelect
                  value={filters.vendor_id}
                  onChange={(id) => set("vendor_id", id)}
                  items={lookup.vendors}
                  getLabel={(v) => v.name || ""}
                  getSearchText={(v) => `${v.name || ""} ${v.vendor_code || ""}`}
                  placeholder={needs("vendor_id") ? "— Pick a vendor —" : "All"}
                  searchPlaceholder="Search vendor…"
                  testid="rpt-vendor"
                />
              </Field>
            )}
            <Field label="Status">
              <select value={filters.status} onChange={e => set("status", e.target.value)}
                data-testid="rpt-status" className={inp}>
                <option value="">All</option>
                <option value="Draft">Draft</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Paid">Paid</option>
                <option value="Posted">Posted</option>
                <option value="Outstanding">Outstanding</option>
              </select>
            </Field>
            {/* 2026-Feb — Line-level free-text filters exposed only for
                the Salary / Store-Rent detailed registers. The backend
                already looks at `month` on Salary lines and
                `rent_period` on Store Rent lines. */}
            {reportId === "salary-payment-register" && (
              <Field label="Month">
                <input type="text" value={filters.month}
                  data-testid="rpt-month"
                  placeholder='e.g. "Jun-26"'
                  onChange={e => set("month", e.target.value)}
                  className={inp} />
              </Field>
            )}
            {reportId === "store-rent-register" && (
              <Field label="Rent Period">
                <input type="text" value={filters.rent_period}
                  data-testid="rpt-rent-period"
                  placeholder='e.g. "Apr-26"'
                  onChange={e => set("rent_period", e.target.value)}
                  className={inp} />
              </Field>
            )}
            {meta?.module === "Audit" && (
              <Field label="Module">
                <select value={filters.module} onChange={e => set("module", e.target.value)}
                  data-testid="rpt-audit-module" className={inp}>
                  <option value="">All modules</option>
                  {AUDIT_MODULES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </Field>
            )}
            {/* Warehouse Report-specific filters */}
            {meta?.module === "Warehouse" && (
              <>
                <Field label="Warehouse">
                  <select value={filters.warehouse_id} onChange={e => set("warehouse_id", e.target.value)}
                    data-testid="rpt-warehouse" className={inp}>
                    <option value="">All</option>
                    {lookup.warehouses
                      .filter(w => !filters.branch_id || w.branch_id === filters.branch_id)
                      .map(w => <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>)}
                  </select>
                </Field>
                {reportId !== "warehouse-asset-register"
                  && reportId !== "warehouse-repair-report"
                  && reportId !== "warehouse-transfer-report" && (
                    <Field label="Item">
                      <select value={filters.item_id} onChange={e => set("item_id", e.target.value)}
                        data-testid="rpt-item" className={inp}>
                        <option value="">All items</option>
                        {lookup.items.slice(0, 500).map(it =>
                          <option key={it.id} value={it.id}>{it.item_code} — {it.name}</option>)}
                      </select>
                    </Field>
                )}
                {reportId === "warehouse-movement-report" && (
                  <Field label="Movement Type">
                    <select value={filters.movement_type} onChange={e => set("movement_type", e.target.value)}
                      data-testid="rpt-movement-type" className={inp}>
                      <option value="">All types</option>
                      <option value="purchase_receipt">Purchase Receipt</option>
                      <option value="inventory_issue">Inventory Issue</option>
                      <option value="asset_issue">Asset Issue</option>
                      <option value="inventory_return">Inventory Return</option>
                      <option value="asset_return">Asset Return</option>
                      <option value="warehouse_transfer">Warehouse Transfer</option>
                      <option value="asset_repair_sent">Repair Sent</option>
                      <option value="asset_repair_return">Repair Return</option>
                    </select>
                  </Field>
                )}
                {reportId === "warehouse-stock-report" && (
                  <Field label="Stock Status">
                    <select value={filters.status} onChange={e => set("status", e.target.value)}
                      data-testid="rpt-stock-status" className={inp}>
                      <option value="">All</option>
                      <option value="positive">Positive only</option>
                      <option value="zero">Zero only</option>
                    </select>
                  </Field>
                )}
              </>
            )}
          </div>

          <div className="mt-3 flex gap-2">
            <button data-testid="rpt-run" onClick={runReport} disabled={running}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
              <Play size={13} /> {running ? "Running…" : "Run Report"}
            </button>
          </div>
        </div>

        {/* Horizontal report catalog (search + module groups + chip cards) */}
        <div className="bg-white border border-slate-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Select Report</span>
              <span className="text-[10px] text-slate-400">
                {reportSearch ? `${totalFiltered}/${catalog.length}` : `${catalog.length} reports`}
              </span>
            </div>
            <div className="relative w-full sm:w-72">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                data-testid="report-search"
                value={reportSearch}
                onChange={e => setReportSearch(e.target.value)}
                placeholder="Search reports by name, module, id…"
                className="w-full pl-7 pr-7 py-1.5 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none"
              />
              {reportSearch && (
                <button onClick={() => setReportSearch("")} data-testid="report-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {Object.entries(groupedCatalog).map(([mod, list]) => (
              <div key={mod}>
                <div className="text-[10px] uppercase tracking-wider text-[#F97316] font-bold mb-2 flex items-center gap-2">
                  <span>{mod}</span>
                  <span className="text-slate-300 font-normal">·</span>
                  <span className="text-slate-400 font-normal">{list.length}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {list.map(r => {
                    const active = reportId === r.id;
                    return (
                      <button key={r.id} data-testid={`report-pick-${r.id}`} onClick={() => setReportId(r.id)}
                        title={r.name}
                        className={`text-left text-xs px-3 py-2 border transition-colors max-w-[260px] truncate ${
                          active
                            ? "bg-[#0B1F3A] border-[#0B1F3A] text-white font-bold shadow-sm"
                            : "bg-white border-slate-300 text-slate-700 hover:border-[#F97316] hover:bg-orange-50"
                        }`}>
                        {r.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {totalFiltered === 0 && reportSearch && (
              <div className="text-xs text-slate-500 italic text-center py-4">
                No reports match &ldquo;{reportSearch}&rdquo;
              </div>
            )}
          </div>
        </div>

        <div data-testid="rpt-info" className="bg-white border border-slate-200 px-4 py-6 mt-3 text-center text-slate-500 text-xs flex flex-col items-center gap-2">
          <FileBarChart2 size={28} className="text-slate-300" />
          Pick a report, set the filters, and click <span className="font-mono">Run Report</span>.
          <span>Results will open in a new tab beside this one.</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Result view — rendered inside a dynamic workspace tab. Holds the data the
   parent gave it, formats numbers, and exposes its own "Export to Excel"
   button that hits /reports/export with the snapshot filters.
   ──────────────────────────────────────────────────────────────────────── */

function ReportResultView({ reportId, filters, data: initialData, pdfCapable = false, onClose }) {
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(initialData);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  // Client-side status post-filter — narrows the on-screen grid AND the
  // Export-to-Excel payload without re-running the report. Defaults to "All".
  const [statusFilter, setStatusFilter] = useState(filters.status || "All");
  // Additional per-report post-run filters. Each entry maps a column key →
  // selected value. Auto-detected based on reportId so the same UI scales
  // to new reports later. Defaults to "All" for every detected filter.
  const [extraFilters, setExtraFilters] = useState({});
  // Universal free-text search added 2026-02-22 per user request:
  // "After Running -- Please give option for search and Member/Holder Type
  //  Search". Searches across every cell in the grid (stringified) so the
  // operator can quickly find an asset / vendor / batch by any visible
  // value without having to scroll the table. Case-insensitive substring
  // match. Empty string disables the search.
  const [searchText, setSearchText] = useState("");

  // Detect which key on each row carries the voucher status. Different reports
  // use different keys (`status`, `approval_status`, `payment_status`).
  const statusKey = useMemo(() => {
    const keys = (data.columns || []).map((c) => c.key);
    if (keys.includes("approval_status")) return "approval_status";
    if (keys.includes("status")) return "status";
    if (keys.includes("payment_status")) return "payment_status";
    return "";
  }, [data.columns]);

  // Distinct status values present in the data, used to populate the dropdown.
  // Always prepend "All". Sorted for stable order.
  const statusOptions = useMemo(() => {
    if (!statusKey) return [];
    const s = new Set();
    for (const r of data.rows || []) {
      const v = (r[statusKey] || "").toString().trim();
      if (v) s.add(v);
    }
    return ["All", ...[...s].sort()];
  }, [data.rows, statusKey]);

  // Per-report extra filter declarations. Each item:
  //   { key, label, type: "distinct" | "yesno", testid }
  // - "distinct": dropdown of unique non-blank values from that column.
  // - "yesno": three-way (All / Yes / No) — Yes = any non-blank value.
  const extraFilterDefs = useMemo(() => {
    const defs = [];
    if (reportId === "vendor-ledger-summary") {
      defs.push({ key: "module", label: "Module", type: "distinct", testid: "rpt-result-module-filter" });
    }
    if (reportId === "approval-audit-summary" || reportId === "approval-audit-history") {
      defs.push({ key: "module_label", label: "Module", type: "distinct", testid: "rpt-result-audit-module-filter" });
      // "Final Status" maps to different column keys per report variant:
      //   - Summary: existing `status` column — already covered by the
      //     generic Status dropdown above. (No extra def needed.)
      //   - History: there is no `status` column (only per-row `action`
      //     and an overall `request_status`); we surface `request_status`
      //     explicitly here.
      const cols = (data.columns || []).map((c) => c.key);
      if (!cols.includes("status") && cols.includes("request_status")) {
        defs.push({ key: "request_status", label: "Final Status", type: "distinct", testid: "rpt-result-final-status-filter" });
      }
    }
    if (reportId === "estimate-register-oos") {
      defs.push({ key: "order_number",       label: "Has Order Number",     type: "yesno", testid: "rpt-result-has-order-filter" });
      defs.push({ key: "mail_approval_date", label: "Has Mail Approval Date", type: "yesno", testid: "rpt-result-has-mail-filter" });
    }
    // Holder Type filter — auto-detected for any report that exposes a
    // `holder_type` or `held_by_type` column. Covers the Asset Holding by
    // Holder report (key=holder_type) and the Asset Register (key=
    // held_by_type, added 2026-02-22). One declaration handles both via
    // the presence-check at the bottom of this useMemo.
    const colKeys = (data.columns || []).map((c) => c.key);
    if (colKeys.includes("holder_type")) {
      defs.push({ key: "holder_type", label: "Holder Type", type: "distinct", testid: "rpt-result-holder-type-filter" });
    } else if (colKeys.includes("held_by_type")) {
      defs.push({ key: "held_by_type", label: "Holder Type", type: "distinct", testid: "rpt-result-holder-type-filter" });
    }
    // Vendor filter — auto-detected for stock reports that surface a
    // `vendor_name` column (Vendor Purchase Rate-wise Stock, Batch-wise
    // Stock Balance, Inventory Register). One-click slice for procurement.
    if (colKeys.includes("vendor_name")) {
      defs.push({ key: "vendor_name", label: "Vendor", type: "distinct", testid: "rpt-result-vendor-filter" });
    }
    // Source filter — distinguishes Bulk-Excel batches from PI-driven ones.
    if (colKeys.includes("source")) {
      defs.push({ key: "source", label: "Source", type: "distinct", testid: "rpt-result-source-filter" });
    }
    // Only keep defs whose column actually exists in the result columns.
    const presentKeys = new Set((data.columns || []).map((c) => c.key));
    return defs.filter((d) => presentKeys.has(d.key));
  }, [reportId, data.columns]);

  const extraOptions = useMemo(() => {
    const out = {};
    for (const d of extraFilterDefs) {
      if (d.type === "yesno") {
        out[d.key] = ["All", "Yes", "No"];
      } else {
        const s = new Set();
        for (const r of data.rows || []) {
          const v = (r[d.key] || "").toString().trim();
          if (v) s.add(v);
        }
        out[d.key] = ["All", ...[...s].sort()];
      }
    }
    return out;
  }, [extraFilterDefs, data.rows]);

  // Filtered rows for display + export. When all filters are "All", returns
  // the original rows unchanged (keeps totals correct in that case).
  const filteredData = useMemo(() => {
    const activeExtras = Object.entries(extraFilters).filter(([, v]) => v && v !== "All");
    const statusActive = statusKey && statusFilter !== "All";
    const q = (searchText || "").trim().toLowerCase();
    if (!statusActive && activeExtras.length === 0 && !q) return data;
    const rows = (data.rows || []).filter((r) => {
      if (statusActive) {
        if ((r[statusKey] || "").toString().trim().toLowerCase() !== statusFilter.toLowerCase()) return false;
      }
      for (const [k, v] of activeExtras) {
        const def = extraFilterDefs.find((d) => d.key === k);
        const cell = (r[k] === null || r[k] === undefined) ? "" : String(r[k]).trim();
        if (def?.type === "yesno") {
          const has = cell !== "";
          if (v === "Yes" && !has) return false;
          if (v === "No" && has) return false;
        } else if (cell.toLowerCase() !== v.toLowerCase()) {
          return false;
        }
      }
      if (q) {
        // Universal free-text search — concatenate every cell on the row,
        // case-insensitive substring match. Done LAST so the search runs
        // on rows that already passed the dropdown filters (faster + the
        // result count matches what's visible).
        const hay = (data.columns || [])
          .map((c) => (r[c.key] === null || r[c.key] === undefined) ? "" : String(r[c.key]).toLowerCase())
          .join(" | ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return { ...data, rows, row_count: rows.length, _filtered: true };
  }, [data, statusKey, statusFilter, extraFilters, extraFilterDefs, searchText]);

  const fmt = (v, isNum) => {
    if (v === null || v === undefined || v === "") return "";
    if (isNum) {
      const n = Number(v);
      if (Number.isNaN(n)) return v;
      return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    }
    return String(v);
  };

  async function refreshReport() {
    if (!reportId) return;
    setRefreshing(true);
    try {
      const res = await api.post("/reports/run", { report_id: reportId, ...filters });
      setData(res.data);
      setLastRefreshed(new Date());
      toast.success("Report refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }

  async function exportPdf() {
    if (!reportId) return;
    setExportingPdf(true);
    try {
      // Server-side PDF — uses the snapshot filters from the run so the
      // exported PDF matches the grid the operator saw on screen.
      const res = await api.post("/reports/export-pdf",
        { report_id: reportId, ...filters },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "PDF export failed");
    } finally {
      setExportingPdf(false);
    }
  }

  async function exportXlsx() {
    if (!reportId) return;
    setExporting(true);
    try {
      // Client-side export. We always export EXACTLY what's currently
      // visible in the grid (`filteredData`) so every post-run filter
      // — Status, Module, Has-Order, Has-Mail, etc. — is honoured. This
      // avoids a backend re-run that would otherwise ignore the
      // client-only filters.
      const XLSX = await import("xlsx");
      const cols = filteredData.columns || [];
      const rows = filteredData.rows || [];
      const aoa = [
        cols.map((c) => c.label || c.key),
        ...rows.map((r) =>
          cols.map((c) => {
            const v = r[c.key];
            if (v === null || v === undefined) return "";
            if (c.num) return Number(v) || 0;
            return v;
          })
        ),
      ];
      // Append a totals row if the report supplies one and we're not on a
      // filtered subset (totals stop being meaningful when rows are filtered out).
      const t = filteredData.totals;
      const isFiltered =
        (statusKey && statusFilter !== "All") ||
        Object.values(extraFilters).some((v) => v && v !== "All");
      if (t && !isFiltered) {
        aoa.push(
          cols.map((c, i) => {
            if (i === 0) return "TOTAL";
            if (c.num && t[c.key] !== undefined) return Number(t[c.key]) || 0;
            return "";
          })
        );
      }
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      // Build filename suffix that reflects active filters so multiple
      // exports of the same report don't overwrite each other.
      const parts = [];
      if (statusKey && statusFilter !== "All") parts.push(statusFilter);
      for (const [k, v] of Object.entries(extraFilters)) {
        if (v && v !== "All") parts.push(`${k}=${v}`);
      }
      const suffix = parts.length ? `__${parts.join("_")}` : "";
      XLSX.writeFile(wb, `${reportId}${suffix}.xlsx`);
      toast.success(
        `Excel downloaded${isFiltered ? ` (${rows.length} filtered row${rows.length === 1 ? "" : "s"})` : ""}`
      );
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div data-testid="report-result-view">
      <Toaster />
      <PageHeader
        eyebrow="Report Result"
        title={data.name || reportId}
        subtitle={`${filteredData.row_count || 0}${statusFilter !== "All" ? ` of ${data.row_count}` : ""} row${filteredData.row_count === 1 ? "" : "s"} · ${filters.from_date || "—"} → ${filters.to_date || "—"} · Last refreshed ${lastRefreshed.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`}
        actions={
          <div className="flex gap-2 items-center flex-wrap">
            {/* Universal in-result search — searches every cell on every
                row. Sits before the dropdowns so it's the first thing the
                operator reaches when looking for something specific. */}
            <label className="flex items-center gap-1.5 text-xs">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Search</span>
              <div className="relative">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Find in results…"
                  data-testid="rpt-result-search"
                  className="pl-6 pr-6 py-1.5 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none w-56 bg-white"
                />
                {searchText && (
                  <button
                    type="button"
                    onClick={() => setSearchText("")}
                    data-testid="rpt-result-search-clear"
                    aria-label="Clear search"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-800 p-0.5"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
            </label>
            {statusKey && statusOptions.length > 1 && (
              <label className="flex items-center gap-1.5 text-xs">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Status</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="rpt-result-status-filter"
                  className="border border-slate-300 px-2 py-1.5 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                >
                  {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            )}
            {extraFilterDefs.map((d) => (
              <label key={d.key} className="flex items-center gap-1.5 text-xs">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{d.label}</span>
                <select
                  value={extraFilters[d.key] || "All"}
                  onChange={(e) => setExtraFilters((p) => ({ ...p, [d.key]: e.target.value }))}
                  data-testid={d.testid}
                  className="border border-slate-300 px-2 py-1.5 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                >
                  {(extraOptions[d.key] || ["All"]).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            ))}
            <button data-testid="rpt-refresh" onClick={refreshReport} disabled={refreshing || exporting}
              className="bg-[#0B1F3A] hover:bg-[#091A30] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button data-testid="rpt-export" onClick={exportXlsx} disabled={exporting || refreshing || exportingPdf}
              className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
              <Download size={13} />
              {exporting
                ? "Exporting…"
                : (() => {
                    const labels = [];
                    if (statusKey && statusFilter !== "All") labels.push(statusFilter);
                    for (const [, v] of Object.entries(extraFilters)) {
                      if (v && v !== "All") labels.push(v);
                    }
                    if (searchText.trim()) labels.push(`"${searchText.trim()}"`);
                    return labels.length ? `Export Filtered (${labels.join(", ")})` : "Export to Excel";
                  })()}
            </button>
            {pdfCapable && (
              <button data-testid="rpt-export-pdf" onClick={exportPdf} disabled={exporting || refreshing || exportingPdf}
                className="bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
                <Download size={13} /> {exportingPdf ? "Exporting…" : "Export to PDF"}
              </button>
            )}
            {onClose && (
              <button data-testid="rpt-close" onClick={onClose}
                className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 text-xs uppercase tracking-wider font-bold">
                Close
              </button>
            )}
          </div>
        }
      />
      <ResultGrid data={filteredData} fmt={fmt} />
    </div>
  );
}

function ResultGrid({ data, fmt }) {
  // Truncate long string cells to ~20 characters with an ellipsis. The full
  // text remains accessible via the `title=` tooltip (hover) so the user can
  // see it without horizontal-scrolling the entire grid. Numbers and short
  // strings render as-is. Per user spec: "max 20 chars then ..., hover shows
  // full text".
  const MAX_CELL_CHARS = 20;
  const truncate = (s) => {
    if (s == null) return "";
    const str = String(s);
    return str.length > MAX_CELL_CHARS ? `${str.slice(0, MAX_CELL_CHARS)}…` : str;
  };
  return (
    <div className="bg-white border border-slate-200">
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between text-xs">
        <span className="font-bold text-slate-700">{data.name}</span>
        <span className="text-slate-500" data-testid="rpt-row-count">
          {data.row_count} row{data.row_count === 1 ? "" : "s"}
          {data._filtered && <span className="ml-2 text-[#F97316] font-bold">· filtered</span>}
        </span>
      </div>
      <div className="overflow-auto" style={{ maxHeight: "60vh" }}>
        <table className="w-full text-[11px] border-collapse" data-testid="rpt-grid">
          <thead className="sticky top-0 z-10">
            <tr>
              {data.columns.map(c => (
                <th key={c.key}
                  className="bg-[#0B1F3A] text-white px-2.5 py-1.5 font-bold uppercase tracking-wider text-[10px] border-r border-[#2c4d7a] whitespace-nowrap text-left">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr><td colSpan={data.columns.length} className="py-6 text-center text-slate-400 text-xs">No rows.</td></tr>
            ) : data.rows.map((r, idx) => (
              <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                {data.columns.map(c => {
                  const raw = r[c.key];
                  const formatted = fmt(raw, c.num);
                  const display = c.num ? formatted : truncate(formatted);
                  const fullText = raw !== null && raw !== undefined ? String(raw) : "";
                  return (
                    <td key={c.key}
                        className={`px-2.5 py-1 border-r border-slate-200 whitespace-nowrap ${c.num ? "text-right tabular-nums" : "max-w-[180px] truncate"}`}
                        title={fullText}>
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {data.totals && Object.keys(data.totals).length > 0 && (
            <tfoot className="sticky bottom-0">
              <tr className="bg-amber-50 font-bold">
                {data.columns.map((c, i) => (
                  <td key={c.key} className={`px-2.5 py-1.5 border-r border-amber-200 whitespace-nowrap ${c.num ? "text-right tabular-nums" : ""}`}>
                    {i === 0 ? "TOTAL" : (c.num && data.totals[c.key] !== undefined ? fmt(data.totals[c.key], true) : "")}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

const inp = "w-full px-2 py-1.5 border border-slate-300 focus:border-[#F97316] focus:outline-none text-xs";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Async Export Strip — for year-end ledger / SI / PI exports that exceed
   the 3,000-row sync cap. Streams a chunked workbook in the background via
   `/api/exports/async/{report}`, polled to ready, then downloaded.
   ──────────────────────────────────────────────────────────────────────── */
const ASYNC_REPORTS = [
  { key: "general-ledger",     label: "General Ledger (Year-End)" },
  { key: "tds-ledger",         label: "TDS Ledger (Year-End)" },
  { key: "sales-invoices",     label: "Sales Invoices (Year-End)" },
  { key: "purchase-invoices",  label: "Purchase Invoices (Year-End)" },
  { key: "payments",           label: "Payments (Year-End)" },
];

function AsyncExportStrip({ filters }) {
  const [busy, setBusy] = useState({});  // {reportKey: labelString}
  async function run(reportKey, reportLabel) {
    setBusy((s) => ({ ...s, [reportKey]: "Starting…" }));
    try {
      await downloadReportAsync(reportKey, {
        from_date: filters.from_date,
        to_date: filters.to_date,
        onProgress: (job) => {
          const done = (job.progress_rows || 0).toLocaleString();
          const total = job.total_rows ? ` / ${job.total_rows.toLocaleString()}` : "";
          setBusy((s) => ({ ...s, [reportKey]: `${done}${total}` }));
        },
      });
      toast.success(`${reportLabel} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Async export failed");
    } finally {
      setBusy((s) => { const c = { ...s }; delete c[reportKey]; return c; });
    }
  }
  return (
    <div className="bg-gradient-to-r from-[#0B1E3F] to-[#102B5E] text-white border border-amber-300/30 p-3 mb-4 shadow"
         data-testid="async-export-strip">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-bold">
            Year-End / Large Dataset Exports
          </div>
          <div className="text-xs text-slate-200 mt-0.5">
            No 3,000-row cap — runs in the background, downloads when ready (filters above apply).
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {ASYNC_REPORTS.map((r) => {
          const label = busy[r.key];
          return (
            <button
              key={r.key}
              data-testid={`async-export-${r.key}`}
              onClick={() => run(r.key, r.label)}
              disabled={!!label}
              className="bg-white/10 hover:bg-amber-400/20 border border-amber-300/40 text-white px-2 py-2 text-[11px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
              title={r.label}
            >
              <Layers size={12} className="text-amber-300" />
              <span className="truncate">{label || r.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
